import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertAction, AlertBody, AlertDescription, AlertIcon, AlertTitle, Box, LightBulbOutline } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import { DefaultUser } from './bonus-content';
import { useActions } from './hooks/use-actions';

interface FeedbackPromptPanelProps {
	bonus: Bonus;
	giveFeedback: (person: DefaultUser) => void;
	requestFeedback: VoidFunction;
}

export const FeedbackPromptPanel: FC<FeedbackPromptPanelProps> = ({ bonus, giveFeedback, requestFeedback }) => {
	const { t } = useTranslation('recognition');
	const { displayFeedbackPanel } = useActions(bonus);
	const { isUserReceiver, isUserReceiversManager } = useContextualizedBonusDetails(bonus);
	const user = useCurrentUser();

	if (!displayFeedbackPanel) {
		return null;
	}

	const request_cta = bonus.traits?.find((trait) => trait.name === 'feedback_candidate')?.data.cta;
	const give_cta = bonus.traits?.find((trait) => trait.name === 'feedback_candidate')?.data.give_cta;

	return (
		<Alert size="md" bg="blue.25" data-testid="feedback-prompt-panel">
			<AlertIcon
				alignSelf="flex-start"
				bg="bg.surface"
				borderRadius="full"
				boxSize={8}
				display="flex"
				flexShrink={0}
				icon={<LightBulbOutline />}
				justifyContent="center"
			/>
			<AlertBody>
				{isUserReceiver && (
					<>
						{request_cta ? (
							<>
								<AlertTitle fontWeight="semibold">{request_cta}</AlertTitle>
								<AlertDescription>{t('bonus_card.feedback.prompt_panel_text_short')}</AlertDescription>
							</>
						) : (
							<AlertDescription>
								<Trans i18nKey="bonus_card.feedback.prompt_panel_text_long" t={t}>
									<Box as="span" fontWeight="semibold">
										{{ name: user.first_name } as any}
									</Box>{' '}
									This could be a good opportunity to ask for feedback and gain insights into your strengths and areas for growth.
								</Trans>
							</AlertDescription>
						)}
						<AlertAction colorScheme="primary" onClick={requestFeedback}>
							{t('bonus_card.feedback.prompt_panel_button')}
						</AlertAction>
					</>
				)}

				{isUserReceiversManager && (
					<>
						{give_cta ? <AlertTitle fontWeight="semibold">{give_cta}</AlertTitle> : <AlertTitle>{t('bonus_card.feedback.prompt_panel_text_give')}</AlertTitle>}

						<AlertAction
							colorScheme="primary"
							data-testid="give-feedback-button"
							onClick={() => {
								// NOTE: displayFeedbackPanel is only true when the user is a manager of the receiver
								// and the receiver is the only receiver so we can safely assume to use the first receiver
								// for populating the give feedback modal
								const receiver = bonus.receivers[0];
								const person: DefaultUser = {
									id: receiver.id,
									imageUrl: receiver.profile_pic_url,
									name: receiver.display_name,
									username: receiver.username,
								};

								giveFeedback(person);
							}}
						>
							{t('bonus_card.feedback.give_feedback_button')}
						</AlertAction>
					</>
				)}
			</AlertBody>
		</Alert>
	);
};
